import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";

import { AppDispatch } from "../../store";
import {
  BreakoutRoomType,
  CapabilityType,
  ClassTypeType,
  ClassroomActionType,
  ClassroomSliceStateType,
  PaneContentType,
  PaneType,
  PollSessionStatusType,
  PollSessionViewResultsAsType,
  RecordingStateType,
  ResourceType,
  ResourceTypeType,
  RootState,
} from "../types";
import { ReactionType, RoleType, ZoomPreferenceType } from "../types/user";
import { getEmptyPaneIndex, getPaneIndexForUser, isUserOnStage } from "../utils/stage";
import {
  REACTION_FILTER_STRATEGY_TO_REACTION_MAP,
  pickUserToReplace,
  pickUsersToAddToAndRemoveFromStage,
} from "../utils/user_picker";

// TODO Because this runs on server and client, window.flightLogger is not defined server-side
// We need to share the logger definition between client and server

const EMPTY_PANE: PaneType = { content_id: undefined, content_type: PaneContentType.Empty };

const REACTION_TIMEOUT_DURATION_MS = 8000;
const LOWER_HAND_TIMEOUT_DURATION_MS = 3000;
export const DEFAULT_TIMER_DURATION_MS = 3 * 60 * 1000;

// Publish actions even if they don't modify the state
function setForcePublish(action: ClassroomActionType) {
  // eslint-disable-next-line no-param-reassign
  action.forcePublish = true;
}

function upsertData<T, U>(
  oldData: { [key: string]: T },
  newData: { [key: string]: T },
  defaultData?: U,
) {
  const newObject = { ...oldData };
  // Add or update keys
  Object.keys(newData).forEach((key) => {
    if (key in newObject) {
      newObject[key] = { ...newObject[key], ...newData[key] };
    } else if (defaultData === undefined) {
      newObject[key] = newData[key];
    } else {
      newObject[key] = { ...defaultData, ...newData[key] };
    }
  });
  // Remove keys that no longer exist
  Object.keys(newObject).forEach((key) => {
    if (!(key in newData)) {
      delete newObject[key];
    }
  });
  return newObject;
}

function setFilter(state: ClassroomSliceStateType, newFilter: string | undefined) {
  Object.values(state.users).forEach((user) => {
    if (
      state.current_filter &&
      state.current_filter !== newFilter &&
      REACTION_FILTER_STRATEGY_TO_REACTION_MAP[state.current_filter] === user.reaction
    ) {
      state.users[user.id].reaction = null;
    }
  });
  state.current_filter = newFilter;
}

function getBreakoutRoomsForActiveBreakout(state: ClassroomSliceStateType) {
  const activeBreakoutSessionId = state.active_breakout_session_id;
  if (!activeBreakoutSessionId) {
    return [];
  }
  const breakoutId = state.breakout_sessions[activeBreakoutSessionId].breakout_id;
  return Object.values(state.breakout_rooms).filter((room) => room.breakout_id === breakoutId);
}

function getAssignedBreakoutRoomIdForUser(state: ClassroomSliceStateType, userId: string) {
  const breakoutRooms = getBreakoutRoomsForActiveBreakout(state);
  return breakoutRooms.find((room) => room.assigned_user_ids.includes(userId))?.id;
}

function getLeastPopulatedBreakoutRoomIdForUser(state: ClassroomSliceStateType, userId: string) {
  const breakoutRooms = getBreakoutRoomsForActiveBreakout(state);
  if (breakoutRooms.length === 0) {
    return;
  }
  const assignedBreakoutRoomId = getAssignedBreakoutRoomIdForUser(state, userId);
  if (assignedBreakoutRoomId) {
    return;
  }
  const leastPopulatedBreakoutRoom = breakoutRooms.reduce((acc, breakoutRoom) => {
    if (breakoutRoom.assigned_user_ids.length < acc.assigned_user_ids.length) {
      return breakoutRoom;
    }
    return acc;
  });
  return leastPopulatedBreakoutRoom.id;
}

function _stopVisitingBreakoutRoom(
  state: ClassroomSliceStateType,
  userId: string,
  breakoutRoomId: string,
) {
  const screenshareResourceId = Object.values(state.resources).find(
    (resource) => resource.type === ResourceTypeType.Screenshare && resource.uploader_id === userId,
  )?.id;

  state.breakout_rooms[breakoutRoomId].visiting_user_ids = state.breakout_rooms[
    breakoutRoomId
  ].visiting_user_ids.filter((id) => id !== userId);

  state.breakout_rooms[breakoutRoomId].panes = state.breakout_rooms[breakoutRoomId].panes.map(
    (pane) => {
      if (
        (pane.content_type === PaneContentType.User && pane.content_id === userId) ||
        (screenshareResourceId &&
          pane.content_type === PaneContentType.Resource &&
          pane.content_id === screenshareResourceId)
      ) {
        return EMPTY_PANE;
      }
      return pane;
    },
  );
}

const initializeUpdateLatestReactionTimeout = () => {
  let timeout: NodeJS.Timeout | undefined;
  let cancelPrevReactionTimeout: () => void;

  return ({ userId, reaction }: { userId: string; reaction: ReactionType | null }) => {
    if (timeout) {
      cancelPrevReactionTimeout();
    }

    let promise = Promise.resolve({});
    if (reaction) {
      promise = new Promise((resolve, reject) => {
        timeout = setTimeout(() => {
          timeout = undefined;
          resolve({ userId, reaction });
        }, REACTION_TIMEOUT_DURATION_MS);
        cancelPrevReactionTimeout = () => {
          clearTimeout(timeout);
          timeout = undefined;
          reject(new Error("Clear reaction aborted"));
        };
      });
    }
    return promise;
  };
};

const updateLatestReactionTimeout = initializeUpdateLatestReactionTimeout();

export const setUserReaction = createAsyncThunk<
  { userId?: string; reaction?: ReactionType },
  { userId: string; reaction: ReactionType },
  { state: RootState; dispatch: AppDispatch }
>("classroom/setUserReaction", async (payload, { getState, dispatch }) => {
  const user = getState().classroom.client.users[payload.userId];
  const nextReaction = user.reaction === payload.reaction ? null : payload.reaction;
  const data = { userId: payload.userId, reaction: nextReaction };
  dispatch(_setUserReaction(data));
  return updateLatestReactionTimeout(data);
});

const initializeUpdateLatestLowerHandTimeout = () => {
  let timeout: NodeJS.Timeout | undefined;
  let cancelPrevLowerHandTimeout: () => void;

  return ({
    userId,
    isSpeaking,
    isHandRaised,
  }: {
    userId: string;
    isSpeaking: boolean;
    isHandRaised: boolean;
  }) => {
    if ((timeout && isSpeaking) || !isHandRaised) {
      return Promise.reject(new Error("Lower hand timeout not necessary"));
    }

    if (timeout) {
      cancelPrevLowerHandTimeout();
    }

    let promise = Promise.resolve({});
    if (isSpeaking && isHandRaised) {
      promise = new Promise((resolve, reject) => {
        timeout = setTimeout(() => {
          timeout = undefined;
          resolve({ userId, isSpeaking });
        }, LOWER_HAND_TIMEOUT_DURATION_MS);
        cancelPrevLowerHandTimeout = () => {
          clearTimeout(timeout);
          timeout = undefined;
          reject(new Error("Lower hand aborted"));
        };
      });
    }
    return promise;
  };
};

const updateLatestLowerHandTimeout = initializeUpdateLatestLowerHandTimeout();

export const setUserIsSpeaking = createAsyncThunk<
  { userId?: string; isSpeaking?: boolean },
  { userId: string; isSpeaking: boolean },
  { state: RootState; dispatch: AppDispatch }
>("classroom/setUserIsSpeaking", async (payload, { getState, dispatch }) => {
  const classRoomState = getState().classroom;
  const user = classRoomState.client.users[payload.userId];
  const isHandRaised = user.is_hand_raised;
  dispatch(_setUserIsSpeaking(payload));
  return updateLatestLowerHandTimeout({ isHandRaised, ...payload });
});

export const replaceUserOnStage = createAsyncThunk(
  "classroom/replaceUserOnStage",
  async (payload: { userId: string }, { getState, dispatch }) => {
    const { userId } = payload;
    const rootState = getState() as RootState;
    const state = rootState.classroom.client;
    const panes = state.stage.panes;
    const paneIndex = getPaneIndexForUser(panes, userId);
    if (paneIndex === -1) return { updated: false };

    const presentVisibleUsers = Object.values(state.users).filter(
      (user) => user.is_present && state.capabilities.isVisibleInClass.includes(user.id),
    );
    const [usersToAddToStage] = pickUsersToAddToAndRemoveFromStage(
      presentVisibleUsers,
      state.participant_bar_user_ids,
      state.capabilities.isAlwaysSubscribedTo,
      state.capabilities.canParticipateInClass,
      panes,
    );
    const newUser = usersToAddToStage[0];
    if (!newUser)
      return {
        updated: false,
        msg: "Everyone in the participant bar is already on the stage.",
      };

    dispatch(_replaceUserOnStage({ paneIndex, newUserId: newUser.id }));
    return { updated: true };
  },
);

export const addUserToParticipantBarAndReplaceOldUser = createAsyncThunk(
  "classroom/addUserToParticipantBarAndReplaceOldUser",
  async (payload: { userId: string; userIdToReplace: string }, { getState, dispatch }) => {
    const { userId, userIdToReplace } = payload;
    const rootState = getState() as RootState;
    const state = rootState.classroom.client;
    const participantBarUserIds = state.participant_bar_user_ids;
    const maxSubscribedStreams = state.max_subscribed_streams;
    const presentVisibleUsers = Object.values(state.users).filter(
      (user) => user.is_present && state.capabilities.isVisibleInClass.includes(user.id),
    );
    const isAlwaysSubscribedToUserIds = state.capabilities.isAlwaysSubscribedTo;

    // Do not add a user if they are already in the participant bar
    if (participantBarUserIds.includes(userId)) {
      return { updated: false, msg: "Selected participant is already in the participant bar." };
    }
    // Do not replace a starred user
    if (isAlwaysSubscribedToUserIds.includes(userIdToReplace || "")) {
      return { updated: false, msg: "Cannot replace a starred user." };
    }
    // Do not replace a user if the participant bar is not full
    if (presentVisibleUsers.length <= maxSubscribedStreams) {
      return { updated: false };
    }

    dispatch(replaceUserInParticipantBar({ userId, userIdToReplace, replaceOnStage: true }));
    return { updated: true };
  },
);

export const addUserToParticipantBarAndToBestPaneOnStage = createAsyncThunk(
  "classroom/addUserToParticipantBarAndToBestPaneOnStage",
  async (payload: { userId: string; pinUserToStage?: boolean }, { getState, dispatch }) => {
    const rootState = getState() as RootState;
    const state = rootState.classroom.client;
    if (state.info.type === ClassTypeType.InPerson) {
      return { updated: false };
    }

    const { userId, pinUserToStage } = payload;
    const participantBarUserIds = state.participant_bar_user_ids;
    const maxSubscribedStreams = state.max_subscribed_streams;
    const presentVisibleUsers = Object.values(state.users).filter(
      (user) => user.is_present && state.capabilities.isVisibleInClass.includes(user.id),
    );
    const isAlwaysSubscribedToUserIds = state.capabilities.isAlwaysSubscribedTo;
    const canParticipateInClassUserIds = state.capabilities.canParticipateInClass;
    const currentPanes = state.stage.panes;

    if (participantBarUserIds.includes(userId) && isUserOnStage(currentPanes, userId)) {
      return { updated: false, msg: "Selected participant is already visible in the classroom." };
    }

    // Find user to replace in participant bar if needed and if possible
    let userIdToReplace = "";
    if (
      presentVisibleUsers.length > maxSubscribedStreams &&
      !participantBarUserIds.includes(userId)
    ) {
      const userToReplace = pickUserToReplace(
        presentVisibleUsers,
        isAlwaysSubscribedToUserIds,
        currentPanes,
        participantBarUserIds,
      );
      if (userToReplace) {
        userIdToReplace = userToReplace.id;
        dispatch(replaceUserInParticipantBar({ userId, userIdToReplace }));
      } else {
        return { updated: false, msg: "The users in the participant bar cannot be replaced." };
      }
    }

    // Find spot on stage if possible
    let paneIndex = -1;
    // If the user we are replacing is on stage, then use their spot
    const paneIndexOfUserToReplace = getPaneIndexForUser(currentPanes, userIdToReplace);
    if (paneIndexOfUserToReplace !== -1) {
      paneIndex = paneIndexOfUserToReplace;
    } else {
      // Otherwise, find an empty spot (excluding reserved panes)
      const emptyPaneIndex = getEmptyPaneIndex(currentPanes);
      if (emptyPaneIndex !== -1) {
        paneIndex = emptyPaneIndex;
      } else {
        // Otherwise, find a user to replace on stage
        const [, usersToRemoveFromStage] = pickUsersToAddToAndRemoveFromStage(
          presentVisibleUsers,
          participantBarUserIds,
          isAlwaysSubscribedToUserIds,
          canParticipateInClassUserIds,
          currentPanes,
        );
        if (usersToRemoveFromStage.length > 0) {
          paneIndex = getPaneIndexForUser(currentPanes, usersToRemoveFromStage[0].id);
        }
      }
    }

    if (paneIndex !== -1) {
      dispatch(
        setStagePane({
          index: paneIndex,
          contentId: userId,
          contentType: PaneContentType.User,
          isPinned: pinUserToStage,
        }),
      );
      dispatch(setCurrentFilter({ filterStrategy: "" }));
      return { updated: true };
    }

    return presentVisibleUsers.length <= maxSubscribedStreams
      ? { updated: false, msg: "There is no available pane for this user." }
      : { updated: true };
  },
);

export const addUserToParticipantBarAndToSpecificPaneOnStage = createAsyncThunk(
  "classroom/addUserToParticipantBarAndToSpecificPaneOnStage",
  async (payload: { userId: string; paneIndex: number }, { getState, dispatch }) => {
    const { userId, paneIndex } = payload;
    const rootState = getState() as RootState;
    const state = rootState.classroom.client;
    const participantBarUserIds = state.participant_bar_user_ids;
    const maxSubscribedStreams = state.max_subscribed_streams;
    const presentVisibleUsers = Object.values(state.users).filter(
      (user) => user.is_present && state.capabilities.isVisibleInClass.includes(user.id),
    );
    const isAlwaysSubscribedToUserIds = state.capabilities.isAlwaysSubscribedTo;
    const currentPanes = state.stage.panes;

    // Find user to replace in participant bar if needed and if possible
    if (
      presentVisibleUsers.length > maxSubscribedStreams &&
      !participantBarUserIds.includes(userId)
    ) {
      let userIdToReplace: string | undefined;

      // Replace user in specified pane (only if the user is a non-starred user)
      const paneUserId = currentPanes[paneIndex].content_id;
      if (
        currentPanes[paneIndex].content_type === PaneContentType.User &&
        !isAlwaysSubscribedToUserIds.includes(paneUserId || "")
      ) {
        userIdToReplace = paneUserId;
      } else {
        // Otherwise, pick a user to replace
        const userToReplace = pickUserToReplace(
          presentVisibleUsers,
          isAlwaysSubscribedToUserIds,
          currentPanes,
          participantBarUserIds,
        );
        userIdToReplace = userToReplace?.id;
      }

      if (!userIdToReplace) {
        return { updated: false, msg: "The users in the participant bar cannot be replaced." };
      }
      dispatch(replaceUserInParticipantBar({ userId, userIdToReplace }));
    }

    dispatch(
      setStagePane({ index: paneIndex, contentId: userId, contentType: PaneContentType.User }),
    );
    dispatch(setCurrentFilter({ filterStrategy: "" }));
    return { updated: true };
  },
);

export const setParticipantBarUserIds = createAsyncThunk(
  "classroom/setParticipantBarUserIds",
  async (
    payload: { userIds: string[]; leaderId: string; updateFilterCount?: boolean },
    { getState, dispatch },
  ) => {
    const { userIds, updateFilterCount } = payload;
    const rootState = getState() as RootState;
    const state = rootState.classroom.client;
    let response = { updated: true, msg: "" };
    if (
      userIds.length > 0 &&
      userIds.every((userId) => state.participant_bar_user_ids.includes(userId))
    ) {
      response = {
        updated: false,
        msg: "Selected participants are already in all available spots in the participant bar.",
      };
    }
    dispatch(_setParticipantBarUserIds({ userIds, updateFilterCount }));
    return response;
  },
);

export const initialClassroomState: ClassroomSliceStateType = {
  active_breakout_session_id: undefined,
  active_timeline_segment_id: undefined,
  av_service: undefined,
  breakouts: {},
  breakout_rooms: {},
  breakout_sessions: {},
  capabilities: {
    [CapabilityType.CAN_ACCESS_MONITORING_DASHBOARD]: [],
    [CapabilityType.CAN_ALWAYS_RESET_CLASS]: [],
    [CapabilityType.CAN_BECOME_TEACHER]: [],
    [CapabilityType.CAN_CHANGE_BANDWIDTH_MODE]: [],
    [CapabilityType.CAN_CONTROL_CLASSROOM]: [],
    [CapabilityType.CAN_EDIT_CLASS]: [],
    [CapabilityType.CAN_ENTER_CLASSROOM]: [],
    [CapabilityType.CAN_MENTION_EVERYONE_IN_CHAT]: [],
    [CapabilityType.CAN_PARTICIPATE_IN_CLASS]: [],
    [CapabilityType.CAN_REFRESH_AV]: [],
    [CapabilityType.CAN_REPUBLISH_STREAMS]: [],
    [CapabilityType.CAN_RESET_CLASS]: [],
    [CapabilityType.CAN_SEE_POLL_RESPONSES_AS_TEACHER]: [],
    [CapabilityType.CAN_START_RECORDING]: [],
    [CapabilityType.CAN_VISIT_BREAKOUTS]: [],
    [CapabilityType.IS_ALWAYS_SUBSCRIBED_TO]: [],
    [CapabilityType.IS_VISIBLE_IN_CLASS]: [],
  },
  chats: {},
  current_filter: "",
  enable_student_drawing: false,
  info: {
    id: "-1",
    allow_student_no_video: false,
    enable_student_audio_only: true,
    branding: undefined,
    course: {
      id: undefined,
      course_code: "",
    },
    date: null,
    disable_slideshow_recording: false,
    end_time: null,
    flight_server: null,
    flight_monitoring_server: null,
    is_in_flight: true,
    is_open: false,
    org: {
      id: undefined,
      name: "",
      short_name: "",
    },
    room_id: "",
    section: {
      id: undefined,
      is_default: false,
      flight_group: "default",
    },
    should_record_session: false,
    title: "",
    type: ClassTypeType.Virtual,
  },
  latest_caption: { text: "", created_on: 0, room_id: "" },
  licode_server: undefined,
  max_subscribed_streams: 12,
  opentok_session_id: undefined,
  participant_bar_user_ids: [],
  polls: {},
  poll_responses: {},
  poll_sessions: {},
  preclass_groups: {},
  preclass_resource_id: undefined,
  presenting_user_ids: [],
  ptt_mode: true,
  recording_session_id: undefined,
  recording_state: RecordingStateType.NotRecording,
  resources: {},
  stage: { panes: [EMPTY_PANE] },
  timeline: { id: undefined, sections: [] },
  timer: {
    durationMs: DEFAULT_TIMER_DURATION_MS,
    timeRemainingWhenPausedMs: DEFAULT_TIMER_DURATION_MS,
    endsOn: null,
    active: false,
    open: false,
  },
  users: {},
  version: null,
};

const classroomSlice = createSlice({
  name: "classroom",
  initialState: initialClassroomState,
  reducers: {
    // Dispatched when a user loads the application.
    join(state, action) {
      const { userId, isPresentationView } = action.payload;
      if (isPresentationView) {
        if (!state.presenting_user_ids) {
          state.presenting_user_ids = [];
        }
        if (!state.presenting_user_ids.includes(userId)) {
          state.presenting_user_ids.push(userId);
        }
        return;
      }

      if (state.users[userId]) {
        state.users[userId].is_present = true;
        state.users[userId].last_entered_at = Date.now();
      }
    },

    leave(state, action) {
      const { userId, isPresentationView } = action.payload;
      if (isPresentationView) {
        if (state.presenting_user_ids) {
          state.presenting_user_ids = state.presenting_user_ids.filter((id) => id !== userId);
        }
        return;
      }

      // When a user is unenrolled, state.users[userId] does not exist
      if (state.users[userId]) {
        state.users[userId].is_present = false;
        state.users[userId].reaction = null;
        state.users[userId].is_hand_raised = false;
        state.users[userId].is_speaking = false;
        for (let i = 0; i < state.stage.panes.length; i += 1) {
          if (state.stage.panes[i].content_id === userId) {
            state.stage.panes[i] = EMPTY_PANE;
          }
        }
        Object.values(state.breakout_rooms).forEach((breakoutRoom) => {
          breakoutRoom.panes.forEach((pane, index) => {
            if (pane.content_id === userId) {
              state.breakout_rooms[breakoutRoom.id].panes[index] = EMPTY_PANE;
            }
          });
        });
      }
    },

    // Help
    sendPubsubMessage(state, action) {
      setForcePublish(action as ClassroomActionType);
      return state;
    },

    softReset(state) {
      // TODO: we should reset everybody's AV?
      state.active_breakout_session_id = undefined;
      state.active_timeline_segment_id = undefined;
      state.breakout_sessions = {};
      state.stage = {
        panes: Array(8).fill({ content_id: undefined, content_type: PaneContentType.Empty }),
      };
      Object.values(state.users).forEach((user) => {
        state.users[user.id].filter_count = 0;
        state.users[user.id].is_hand_raised = false;
        state.users[user.id].reaction = null;
        state.users[user.id].vote = null;
      });
      state.presenting_user_ids = [];
      // Reset breakout group indicators
      classroomSlice.caseReducers.clearAllBreakoutGroupTitles(state);
    },

    hardReset(state) {
      classroomSlice.caseReducers.softReset(state);
      state.chats = {};
      state.poll_responses = {};
      state.poll_sessions = {};
      const resourcesWithWhiteboards = [
        ResourceTypeType.Whiteboard,
        ResourceTypeType.Pdf,
        ResourceTypeType.HtmlSlide,
      ];
      Object.values(state.resources).forEach((resource) => {
        if (resourcesWithWhiteboards.includes(resource.type)) {
          state.resources[resource.id].reset_whiteboard = true;
        }

        const uploader = state.users[resource.uploader_id];
        const uploadedByStudent = uploader?.role === "student";
        if (!(resource.precreated || resource.is_breakout_group_notes) || uploadedByStudent) {
          delete state.resources[resource.id];
        }
      });
    },

    // Participant bar
    _setParticipantBarUserIds(
      state,
      action: { payload: { userIds: string[]; updateFilterCount?: boolean } },
    ) {
      const { userIds, updateFilterCount } = action.payload;
      // Remove users from stage if necessary
      state.stage.panes.forEach((pane, i) => {
        if (
          pane.content_type === PaneContentType.User &&
          !userIds.includes(pane.content_id || "")
        ) {
          state.stage.panes[i] = EMPTY_PANE;
        }
      });

      state.participant_bar_user_ids = userIds;

      if (updateFilterCount) {
        userIds.forEach((userId) => {
          state.users[userId].filter_count = (state.users[userId].filter_count || 0) + 1;
        });
      }
    },

    replaceUserInParticipantBar(
      state,
      action: { payload: { userId: string; userIdToReplace: string; replaceOnStage?: boolean } },
    ) {
      const { userId, userIdToReplace, replaceOnStage } = action.payload;
      state.participant_bar_user_ids = state.participant_bar_user_ids.map((id) =>
        id === userIdToReplace ? userId : id,
      );
      state.stage.panes.forEach((pane, i) => {
        if (pane.content_type === PaneContentType.User && pane.content_id === userIdToReplace) {
          if (replaceOnStage) {
            state.stage.panes[i].content_id = userId;
          } else {
            state.stage.panes[i] = EMPTY_PANE;
          }
        }
      });
      setFilter(state, "");
    },

    setCurrentFilter(state, action: { payload: { filterStrategy: string | undefined } }) {
      const { filterStrategy } = action.payload;
      setFilter(state, filterStrategy);
    },

    // Stage management
    _replaceUserOnStage(state, action: { payload: { paneIndex: number; newUserId: string } }) {
      const { paneIndex, newUserId } = action.payload;
      const pane = state.stage.panes[paneIndex];
      pane.content_id = newUserId;
      pane.is_pinned = false;
      setFilter(state, "");
    },

    featureBreakoutRoomResource(state, action: { payload: { breakoutRoomId: string } }) {
      const { breakoutRoomId } = action.payload;

      const breakoutRoom = state.breakout_rooms[breakoutRoomId];
      const resourceIds = breakoutRoom.resource_ids;
      const breakoutNotes = Object.values(state.resources).find(
        (resource) => resourceIds.includes(resource.id) && resource.is_breakout_group_notes,
      );

      if (breakoutNotes) {
        state.stage.panes = [
          {
            content_id: breakoutNotes.id,
            content_type: PaneContentType.Resource,
          },
        ];
      }
    },

    featureContentInOneUp(
      state,
      action: {
        payload: {
          contentId: string | undefined;
          contentType: PaneContentType;
          breakoutRoomId?: string;
          ownerId?: string;
        };
      },
    ) {
      const { contentId, contentType, breakoutRoomId, ownerId } = action.payload;

      const panes = [
        {
          content_id: contentId,
          content_type: contentType,
        },
      ];

      if (breakoutRoomId) {
        state.breakout_rooms[breakoutRoomId].panes = panes;
      } else {
        state.stage.panes = panes;
      }

      // If a resource is featured on stage, set the owner id
      if (contentType === PaneContentType.Resource && contentId && ownerId) {
        state.resources[contentId].owner_id = ownerId;
      }
    },

    setFeaturedInModal(state, action) {
      const { contentId } = action.payload;
      state.stage.featured_in_modal = contentId;
    },

    setStagePane(
      state,
      action: {
        payload: {
          index: number;
          contentId: string | undefined;
          contentType: PaneContentType;
          breakoutRoomId?: string;
          ownerId?: string;
          isPinned?: boolean;
          hiddenInPresentationView?: boolean;
          targetUserId?: string; // this is needed for permissions
        };
      },
    ) {
      const {
        index,
        contentId,
        contentType,
        breakoutRoomId,
        ownerId,
        isPinned,
        hiddenInPresentationView,
      } = action.payload;

      const newPane = {
        content_id: contentId,
        content_type: contentType,
        is_pinned: isPinned || false,
        hidden_in_presentation_view: hiddenInPresentationView || false,
      };

      if (breakoutRoomId) {
        // Duplicate panes are not allowed
        state.breakout_rooms[breakoutRoomId].panes.forEach((pane, i) => {
          if (pane.content_id === contentId && pane.content_type === contentType) {
            state.breakout_rooms[breakoutRoomId].panes[i] = EMPTY_PANE;
          }
        });
        while (state.breakout_rooms[breakoutRoomId].panes.length <= index) {
          state.breakout_rooms[breakoutRoomId].panes.push(EMPTY_PANE);
        }
        state.breakout_rooms[breakoutRoomId].panes[index] = newPane;
      } else {
        // Duplicate panes are not allowed
        state.stage.panes.forEach((pane, i) => {
          if (pane.content_id === contentId && pane.content_type === contentType) {
            state.stage.panes[i] = EMPTY_PANE;
          }
        });
        while (state.stage.panes.length <= index) {
          state.stage.panes.push(EMPTY_PANE);
        }
        state.stage.panes[index] = newPane;
      }

      // If a resource is featured on stage, set the owner id
      if (contentType === PaneContentType.Resource && contentId && ownerId) {
        state.resources[contentId].owner_id = ownerId;
      }
    },

    setStagePaneIsPinned(
      state,
      action: {
        payload: {
          index: number;
          isPinned: boolean;
        };
      },
    ) {
      const { index, isPinned } = action.payload;
      state.stage.panes[index].is_pinned = isPinned;
    },

    setAllStagePanes(
      state,
      action: { payload: { panes: PaneType[]; ownerId?: string; breakoutRoomId?: string } },
    ) {
      const { panes, ownerId, breakoutRoomId } = action.payload;
      if (breakoutRoomId) {
        state.breakout_rooms[breakoutRoomId].panes = panes;
      } else {
        state.stage.panes = panes;
      }

      // If a resource is featured on stage, set the owner id
      panes.forEach((pane) => {
        if (pane.content_type === PaneContentType.Resource && pane.content_id && ownerId) {
          if (state.resources[pane.content_id]) {
            state.resources[pane.content_id].owner_id = ownerId;
          }
        }
      });
    },

    clearAllStagePanes(state, action: { payload: { breakoutRoomId?: string } }) {
      const { breakoutRoomId } = action.payload;
      if (breakoutRoomId) {
        state.breakout_rooms[breakoutRoomId].panes.forEach((pane, i) => {
          state.breakout_rooms[breakoutRoomId].panes[i] = EMPTY_PANE;
        });
      } else {
        state.stage.panes.forEach((pane, i) => {
          state.stage.panes[i] = EMPTY_PANE;
        });
      }
    },

    // Classroom settings
    setActiveTimelineSegmentId(state, action) {
      const { id } = action.payload;
      state.active_timeline_segment_id = id;
    },

    setAvService(state, action) {
      const { avService } = action.payload;
      state.av_service = avService;
    },

    setClassType(state, action) {
      const { classType } = action.payload;
      state.info.type = classType;
    },

    setEnableStudentDrawing(state, action: { payload: { isEnabled: boolean; userId?: string } }) {
      const { isEnabled, userId } = action.payload;
      state.enable_student_drawing = isEnabled;
      if (userId) {
        state.users[userId].enable_student_drawing = isEnabled;
      }
    },

    setIsInFlight(state, action) {
      const { isInFlight } = action.payload;
      state.info.is_in_flight = isInFlight;
    },

    // AV
    setBandwidthModeForUser(state, action) {
      const { userId, bandwidthMode } = action.payload;
      state.users[userId].bandwidth_mode = bandwidthMode;
    },

    setBlurBackground(state, action) {
      const { userId, blurBackground } = action.payload;
      state.users[userId].blur_background = blurBackground;
    },

    setPttModeForClass(state, action) {
      const { pttMode } = action.payload;
      state.ptt_mode = pttMode;
    },

    setPttModeForUser(state, action) {
      const { userId, pttMode } = action.payload;
      state.users[userId].ptt_mode = pttMode;
    },

    setMaxSubscribedStreams(state, action) {
      const { maxSubscribedStreams } = action.payload;
      state.max_subscribed_streams = maxSubscribedStreams;
    },

    setUserCameraError(state, action) {
      const { userId, cameraError } = action.payload;
      state.users[userId].av.camera_error = cameraError;
    },

    setUserConnectionQuality(state, action) {
      const { userId, connectionQuality } = action.payload;
      state.users[userId].av.connection_quality = connectionQuality;
    },

    setUserConnectionState(state, action) {
      const { userId, connectionState } = action.payload;
      state.users[userId].av.connection_state = connectionState;
    },

    setUserPubsubConnectionQuality(state, action) {
      const { userId, pubsubConnectionQuality } = action.payload;
      state.users[userId].pubsub_connection_quality = pubsubConnectionQuality;
    },

    setUserIsPerformingAvCheck(state, action) {
      const { userId, isPerformingAvCheck } = action.payload;
      state.users[userId].is_performing_av_check = isPerformingAvCheck;
    },

    setUserMicError(state, action) {
      const { userId, micError } = action.payload;
      state.users[userId].av.mic_error = micError;
    },

    setUserStreamState(state, action) {
      const { userId, streamState } = action.payload;
      state.users[userId].av.published_stream_state = streamState;
    },

    setUserPreferredDevices(state, action) {
      const { userId, preferredCameraLabel, preferredMicLabel } = action.payload;
      state.users[userId].pref_camera_device_label = preferredCameraLabel;
      state.users[userId].pref_mic_device_label = preferredMicLabel;
    },

    setUserWhiteboardFontSizePreference(state, action) {
      const { userId, size } = action.payload;
      state.users[userId].whiteboard_font_size_preference = size;
    },

    setUserWhiteboardStrokeSizePreference(state, action) {
      const { userId, size } = action.payload;
      state.users[userId].whiteboard_stroke_size_preference = size;
    },

    muteAllExceptInstructor(state) {
      const isVisibleInClassUserIds = state.capabilities.isVisibleInClass;
      isVisibleInClassUserIds.forEach((userId) => {
        if (state.users[userId].role !== RoleType.Teacher) {
          state.users[userId].ptt_mode = true;
        }
      });
    },

    // Chat
    addChat(state, action) {
      const { id, createdOn, userId, text } = action.payload;
      state.chats[id] = {
        id,
        created_on: createdOn,
        user_id: userId,
        text,
        likes: [],
      };
    },

    likeOrUnlikeChat(state, action) {
      const { id, userId } = action.payload;
      if (userId === state.chats[id].user_id) return;
      const likes = state.chats[id].likes;
      if (likes.includes(userId)) {
        likes.splice(likes.indexOf(userId), 1);
      } else {
        likes.push(userId);
      }
      state.chats[id].likes = likes;
    },

    // Recordings
    setRecordingSessionId(state, action) {
      const { id } = action.payload;
      state.recording_session_id = id;
    },

    setRecordingState(state, action) {
      const { recordingState } = action.payload;
      state.recording_state = recordingState;
    },

    // Breakouts
    addBreakout(state, action) {
      const { id, durationInSeconds, grouping, title, hasFlavors, shareResourceBetweenGroups } =
        action.payload;
      state.breakouts[id] = {
        id,
        duration_in_seconds: durationInSeconds,
        grouping,
        title,
        has_flavors: hasFlavors,
        share_resource_between_groups: shareResourceBetweenGroups,
      };
    },

    clearAllBreakoutGroupTitles(state) {
      Object.values(state.users).forEach((user) => {
        state.users[user.id].breakout_group_title = "";
      });
    },

    setAllBreakoutGroupTitles(state, action) {
      const { breakoutId } = action.payload;
      const breakoutRooms = Object.values(state.breakout_rooms).filter(
        (breakoutRoom) => breakoutRoom.breakout_id === breakoutId,
      );
      breakoutRooms.forEach((breakoutRoom) => {
        breakoutRoom.assigned_user_ids.forEach((userId) => {
          const user = state.users[userId];
          if (user) {
            user.breakout_group_title = breakoutRoom.title;
          }
        });
      });
    },

    setBreakoutRooms(state, action) {
      const { breakoutRooms, shouldDeletePreviousRooms } = action.payload;

      // For breakouts based on workbook assignments: the breakout groups are recreated
      // to ensure students and their resources are correctly assigned
      if (shouldDeletePreviousRooms) {
        const breakoutId = breakoutRooms[0].breakout_id;
        const allBreakoutRooms = Object.values(state.breakout_rooms);
        allBreakoutRooms.forEach((room) => {
          if (room.breakout_id === breakoutId) {
            delete state.breakout_rooms[room.id];
          }
        });
      }

      breakoutRooms.forEach((breakoutRoom: BreakoutRoomType) => {
        state.breakout_rooms[breakoutRoom.id] = {
          ...breakoutRoom,
          visiting_user_ids: [],
          listening_user_ids: [],
          talk_times: {},
        };
      });
    },

    setResources(state, action) {
      const { resources } = action.payload;
      resources.forEach((resource: ResourceType) => {
        state.resources[resource.id] = resource;
      });
    },

    startBreakoutSession(
      state,
      action: {
        payload: { id: string; breakoutId: string; startedOn?: string; endedOn?: string | null };
      },
    ) {
      const { id, breakoutId, startedOn, endedOn } = action.payload;
      state.breakout_sessions[id] = {
        id,
        breakout_id: breakoutId,
        started_on: startedOn || new Date().toISOString(),
        ended_on: endedOn || null,
      };
      state.active_breakout_session_id = id;
    },

    adjustBreakoutSessionDuration(state, action) {
      const { id, timeAdjustmentInSeconds } = action.payload;
      const priorEnd = state.breakout_sessions[id].ended_on;
      if (priorEnd) {
        state.breakout_sessions[id].ended_on = new Date(
          new Date(priorEnd).getTime() + timeAdjustmentInSeconds * 1000,
        ).toISOString();
      }
    },

    moveBreakoutRoomUser(state, action) {
      const { userId, oldBreakoutRoomId, newBreakoutRoomId, newBreakoutRoomTitle } = action.payload;
      const oldBreakoutRoom = state.breakout_rooms[oldBreakoutRoomId];
      let _newBreakoutRoomId = newBreakoutRoomId;
      if (newBreakoutRoomTitle) {
        const matches = Object.values(state.breakout_rooms).filter(
          (breakoutRoom) =>
            breakoutRoom.breakout_id === oldBreakoutRoom.breakout_id &&
            breakoutRoom.title === newBreakoutRoomTitle,
        );
        if (matches.length > 0) {
          _newBreakoutRoomId = matches[0].id;
        }
      }
      if (_newBreakoutRoomId) {
        // Remove user from old breakout room stage
        state.breakout_rooms[oldBreakoutRoomId].panes = oldBreakoutRoom.panes.map((pane) => {
          if (pane.content_type === PaneContentType.User && pane.content_id === userId) {
            return EMPTY_PANE;
          }
          return pane;
        });
        // Remove user from old breakout room
        state.breakout_rooms[oldBreakoutRoomId].assigned_user_ids =
          oldBreakoutRoom.assigned_user_ids.filter((id) => id !== userId);
        state.breakout_rooms[oldBreakoutRoomId].fixed_user_ids =
          oldBreakoutRoom.fixed_user_ids.filter((id) => id !== userId);
        // Add user to new breakout room
        if (!state.breakout_rooms[_newBreakoutRoomId].assigned_user_ids.includes(userId)) {
          state.breakout_rooms[_newBreakoutRoomId].assigned_user_ids.push(userId);
          state.breakout_rooms[_newBreakoutRoomId].fixed_user_ids.push(userId);
        }
      }
    },

    assignUserToLeastPopulatedBreakoutRoom(state, action) {
      const { userId } = action.payload;
      const leastPopulatedBreakoutRoomId = getLeastPopulatedBreakoutRoomIdForUser(state, userId);
      if (leastPopulatedBreakoutRoomId) {
        state.breakout_rooms[leastPopulatedBreakoutRoomId].assigned_user_ids.push(userId);
      }
    },

    setBreakoutRoomIsPinned(
      state,
      action: { payload: { breakoutRoomId: string; isPinned: boolean } },
    ) {
      const { breakoutRoomId, isPinned } = action.payload;
      state.breakout_rooms[breakoutRoomId].is_pinned = isPinned;
    },

    visitBreakoutRoom(state, action) {
      const { userId, breakoutRoomId } = action.payload;
      const breakoutId = state.breakout_rooms[breakoutRoomId].breakout_id;
      Object.values(state.breakout_rooms)
        .filter((breakoutRoom) => breakoutRoom.breakout_id === breakoutId)
        .forEach((breakoutRoom) => {
          if (breakoutRoom.visiting_user_ids.includes(userId)) {
            _stopVisitingBreakoutRoom(state, userId, breakoutRoom.id);
          }
          if (breakoutRoom.listening_user_ids.includes(userId)) {
            state.breakout_rooms[breakoutRoom.id].listening_user_ids =
              breakoutRoom.listening_user_ids.filter((id) => id !== userId);
          }
        });
      if (breakoutRoomId) {
        state.breakout_rooms[breakoutRoomId].visiting_user_ids.push(userId);
      }
    },

    stopVisitingBreakoutRoom(state, action) {
      const { userId, breakoutRoomId } = action.payload;
      _stopVisitingBreakoutRoom(state, userId, breakoutRoomId);
    },

    listenToBreakoutRoom(state, action) {
      const { userId, breakoutRoomId } = action.payload;
      const breakoutId = state.breakout_rooms[breakoutRoomId].breakout_id;
      Object.values(state.breakout_rooms)
        .filter((breakoutRoom) => breakoutRoom.breakout_id === breakoutId)
        .forEach((breakoutRoom) => {
          if (!breakoutRoom.listening_user_ids) {
            state.breakout_rooms[breakoutRoom.id].listening_user_ids = [];
          }
          if (breakoutRoom.listening_user_ids.includes(userId)) {
            state.breakout_rooms[breakoutRoom.id].listening_user_ids =
              breakoutRoom.listening_user_ids.filter((id) => id !== userId);
          }
        });
      if (breakoutRoomId) {
        state.breakout_rooms[breakoutRoomId].listening_user_ids.push(userId);
      }
    },

    stopListeningToBreakoutRoom(state, action) {
      const { userId, breakoutRoomId } = action.payload;
      state.breakout_rooms[breakoutRoomId].listening_user_ids = state.breakout_rooms[
        breakoutRoomId
      ].listening_user_ids.filter((id) => id !== userId);
    },

    endBreakoutSession(state) {
      // TODO: set end time of breakout session?
      state.active_breakout_session_id = undefined;
      Object.values(state.breakout_rooms).forEach((breakoutRoom) => {
        state.breakout_rooms[breakoutRoom.id].visiting_user_ids = [];
        state.breakout_rooms[breakoutRoom.id].listening_user_ids = [];
      });
    },

    // Timer
    playTimer(state, action: { payload: { endsOn: string } }) {
      const { endsOn } = action.payload;
      state.timer.active = true;
      state.timer.open = true;
      state.timer.endsOn = endsOn;
    },

    pauseTimer(state, action: { payload: { timeRemainingMs: number } }) {
      const { timeRemainingMs } = action.payload;
      state.timer.active = false;
      if (state.timer.endsOn) {
        state.timer.timeRemainingWhenPausedMs = timeRemainingMs;
      }
    },

    adjustTimerDuration(
      state,
      action: { payload: { timeAdjustmentInSeconds: number; endsOn: string | null } },
    ) {
      const { timeAdjustmentInSeconds, endsOn } = action.payload;
      if (state.timer.durationMs) {
        state.timer.durationMs += timeAdjustmentInSeconds * 1000;
      }
      if (state.timer.timeRemainingWhenPausedMs) {
        state.timer.timeRemainingWhenPausedMs += timeAdjustmentInSeconds * 1000;
      }
      if (state.timer.endsOn) {
        state.timer.endsOn = endsOn;
      }
    },

    endTimer(state) {
      state.timer.durationMs = DEFAULT_TIMER_DURATION_MS;
      state.timer.timeRemainingWhenPausedMs = DEFAULT_TIMER_DURATION_MS;
      state.timer.endsOn = null;
      state.timer.open = false;
      state.timer.active = false;
    },

    // User
    addBreakoutTalkTime(
      state,
      action: { payload: { userId: string; breakoutRoomId: string; duration: number } },
    ) {
      const { userId, breakoutRoomId, duration } = action.payload;
      const prevTalkTime = state.users[userId].breakout_talk_time || 0;
      state.users[userId].breakout_talk_time = prevTalkTime + duration;
      const talkTimes = state.breakout_rooms[breakoutRoomId].talk_times;
      if (talkTimes) {
        if (userId in talkTimes) {
          state.breakout_rooms[breakoutRoomId].talk_times![userId] += duration;
        } else {
          state.breakout_rooms[breakoutRoomId].talk_times![userId] = duration;
        }
      } else {
        state.breakout_rooms[breakoutRoomId].talk_times = { [userId]: duration };
      }
    },

    addClassroomTalkTime(state, action: { payload: { userId: string; duration: number } }) {
      const { userId, duration } = action.payload;
      const prevTalkTime = state.users[userId].classroom_talk_time || 0;
      state.users[userId].classroom_talk_time = prevTalkTime + duration;
    },

    setUserHasClassroomInFocus(
      state,
      action: { payload: { userId: string; hasClassroomInFocus: boolean } },
    ) {
      const { userId, hasClassroomInFocus } = action.payload;
      state.users[userId].has_classroom_in_focus = hasClassroomInFocus;
    },

    setUserPresenceData(
      state,
      action: { payload: { userId: string; secondsPresent: number; enterClassTime: number } },
    ) {
      const { userId, secondsPresent, enterClassTime } = action.payload;
      state.users[userId].seconds_present = secondsPresent;
      state.users[userId].enter_class_time = enterClassTime;
    },

    // Reactions and signals
    setUserVote(state, action) {
      const { userId, vote } = action.payload;
      if (state.users[userId].vote === vote) {
        state.users[userId].vote = null;
      } else {
        state.users[userId].vote = vote;
      }
    },

    setUserZoomPreference(
      state,
      action: { payload: { userId: string; zoomPreference: ZoomPreferenceType } },
    ) {
      const { userId, zoomPreference } = action.payload;
      state.users[userId].zoom_preference = zoomPreference;
    },

    toggleUserHandRaised(state, action) {
      const { userId } = action.payload;
      const oldHandRaised = state.users[userId].is_hand_raised;
      state.users[userId].is_hand_raised = !oldHandRaised;
    },

    clearAllSignals(state) {
      Object.keys(state.users).forEach((key: string) => {
        const userId = parseInt(key, 10);
        state.users[userId].reaction = null;
        state.users[userId].vote = null;
        state.users[userId].is_hand_raised = false;
      });
      setFilter(state, "");
    },

    // Polls
    createNewPoll(state, action) {
      const { id, question, type, student_visible, multiple_response, poll_choices, grading_type } =
        action.payload;
      state.polls[id] = {
        id,
        question,
        title: question,
        type,
        student_visible,
        multiple_response,
        poll_choices,
        grading_type,
      };
    },

    startPollSession(state, action: { payload: { id: string; pollId: string } }) {
      const { id, pollId } = action.payload;
      state.poll_sessions[id] = {
        id,
        poll_id: pollId,
        started_at: Date.now(),
        ended_at: null,
        status: PollSessionStatusType.InProgress,
        view_results_as: PollSessionViewResultsAsType.Pie,
      };
    },

    endPollSession(state, action) {
      const { id, endedAt } = action.payload;
      state.poll_sessions[id].status = PollSessionStatusType.Completed;
      state.poll_sessions[id].ended_at = endedAt;
    },

    setPollSessionViewResultsAs(state, action) {
      const { id, viewResultsAs } = action.payload;
      state.poll_sessions[id].view_results_as = viewResultsAs;
    },

    createOrUpdatePollResponse(
      state,
      action: {
        payload: {
          id: string;
          pollSessionId?: string;
          userId?: string;
          response?: string | number[];
          isDraft?: boolean;
          submittedAt?: number | null;
          hideFromStudents?: string[]; // an array of fields to be hidden from other students when broadcasting (used by the server)
        };
      },
    ) {
      const { id, pollSessionId, userId, response, isDraft, submittedAt } = action.payload;
      const pollResponse = state.poll_responses[id];
      state.poll_responses[id] = {
        id,
        poll_session_id: pollSessionId || pollResponse.poll_session_id,
        user_id: userId || pollResponse.user_id,
        response: response || pollResponse.response,
        is_draft: isDraft !== undefined ? isDraft : pollResponse.is_draft,
        submitted_at: submittedAt !== undefined ? submittedAt : pollResponse.submitted_at,
      };
    },

    makePollResponsesVisibleToStudents(state, action: { payload: { pollId: string } }) {
      const { pollId } = action.payload;
      state.polls[pollId].student_visible = true;
    },

    featurePollResponse(
      state,
      action: {
        payload: {
          pollResponseId: string;
          pollSessionId: string;
          response: string;
          userId: string;
        };
      },
    ) {
      const { pollResponseId, pollSessionId, response, userId } = action.payload;
      state.stage.panes.forEach((pane, index) => {
        if (
          pane.content_type === PaneContentType.PollSession &&
          pane.content_id === pollSessionId
        ) {
          state.stage.panes[index].featured_poll_response = {
            id: pollResponseId,
            response,
            user_id: userId,
          };
        }
      });
    },

    hideFeaturedPollResponse(state, action) {
      const { pollResponseId } = action.payload;
      state.stage.panes.forEach((pane, index) => {
        if (
          pane.content_type === PaneContentType.PollSession &&
          pane.featured_poll_response?.id === pollResponseId
        ) {
          state.stage.panes[index].featured_poll_response = undefined;
        }
      });
    },

    // Resources
    addResource(state, action) {
      const { resource } = action.payload;
      state.resources[resource.id] = resource;
    },

    deleteScreenshareResource(state, action) {
      const { resourceId } = action.payload;
      if (
        state.resources[resourceId] &&
        state.resources[resourceId].type === ResourceTypeType.Screenshare
      ) {
        delete state.resources[resourceId];
      }
    },

    setResourceOwnerId(state, action) {
      const { resourceId, ownerId } = action.payload;
      state.resources[resourceId].owner_id = ownerId;
    },

    setResourcePage(state, action) {
      const { resourceId, pageNumber } = action.payload;
      state.resources[resourceId].page = pageNumber;
    },

    setResourcePresentationViewScrollPosition(state, action) {
      const { resourceId, userId, scrollX, scrollY } = action.payload;
      if (!state.resources[resourceId].presentation_view) {
        state.resources[resourceId].presentation_view = {};
      }
      state.resources[resourceId].presentation_view[userId] = {
        scroll_x: scrollX,
        scroll_y: scrollY,
      };
    },

    setResourceTitle(state, action) {
      const { resourceId, title } = action.payload;
      state.resources[resourceId].title = title;
    },

    setResourceVideoState(state, action) {
      const { resourceId, videoState } = action.payload;
      state.resources[resourceId].video_state = videoState;
    },

    setResourceVideoTimeInSeconds(state, action) {
      const { resourceId, videoTimeInSeconds } = action.payload;
      state.resources[resourceId].video_time_in_seconds = videoTimeInSeconds;
    },

    setResourceResetWhiteboard(state, action) {
      const { resourceId, reset } = action.payload;
      state.resources[resourceId].reset_whiteboard = reset;
    },

    // Live captions
    setUserEnableLiveCaptions(state, action) {
      const { userId, enableLiveCaptions } = action.payload;
      state.users[userId].enable_live_captions = enableLiveCaptions;
    },

    setLatestCaption(
      state,
      action: { payload: { text: string; createdOn: number; roomId: string } },
    ) {
      const { text, createdOn, roomId } = action.payload;
      state.latest_caption = { text, created_on: createdOn, room_id: roomId };
    },

    _setUserReaction(state, action) {
      const { userId, reaction } = action.payload;
      state.users[userId].reaction = reaction;
    },

    _setUserIsSpeaking(state, action) {
      const { userId, isSpeaking } = action.payload;
      state.users[userId].is_speaking = isSpeaking;
    },

    // Directly set the entire state. Generally used for publishing.
    directlySetState(state, action) {
      const { newState } = action.payload;
      return newState;
    },

    upsertState(state, action) {
      const { breakouts, breakout_rooms, polls, resources, users, info, ...rest } = action.payload
        .newState as ClassroomSliceStateType;
      // When a breakout is in progress, we don't want to overwrite the panes in the corresponding breakout rooms
      // in case they have been changed by someone.
      if (state.active_breakout_session_id) {
        const breakoutId = state.breakout_sessions[state.active_breakout_session_id].breakout_id;
        Object.values(breakout_rooms).forEach((breakoutRoom) => {
          if (breakoutRoom.breakout_id === breakoutId && breakoutRoom.id in state.breakout_rooms) {
            breakout_rooms[breakoutRoom.id].panes = cloneDeep(
              state.breakout_rooms[breakoutRoom.id].panes,
            );
          }
        });
      }

      // Keep any screenshare resources that exist in the Flight classroom only
      // and ignore any incoming screenshare resources
      const incomingResources = { ...resources };
      const incomingScreenshareIds = new Set();

      Object.entries(resources).forEach(([id, resource]) => {
        if (resource.type === ResourceTypeType.Screenshare) {
          incomingScreenshareIds.add(id);
          delete incomingResources[id];
        }
      });

      Object.entries(state.resources).forEach(([id, resource]) => {
        if (resource.type === ResourceTypeType.Screenshare && !incomingScreenshareIds.has(id)) {
          incomingResources[id] = resource;
        }

        // If the resource is a PDF or HTML slide, we don't want to reset the page number.
        if (
          [ResourceTypeType.Pdf, ResourceTypeType.HtmlSlide].includes(resource.type) &&
          resource.page > 1
        ) {
          if (incomingResources[id]) {
            incomingResources[id].page = resource.page;
          }
        }
      });

      return {
        ...state,
        ...rest,
        info,
        breakouts: upsertData(state.breakouts, breakouts),
        breakout_rooms: upsertData(state.breakout_rooms, breakout_rooms),
        polls: upsertData(state.polls, polls),
        resources: upsertData(state.resources, incomingResources),
        users: upsertData(state.users, users, {
          bandwidth_mode: "high",
          ptt_mode: true,
          av: {
            camera_error: null,
            connection_quality: null,
            connection_state: null,
            published_stream_state: null,
            mic_error: null,
          },
        }),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUserReaction.fulfilled, (state, action) => {
        // clear reaction when applicable
        const { userId, reaction } = action.payload;
        if (
          userId &&
          reaction &&
          reaction === state.users[userId].reaction &&
          !(
            state.current_filter &&
            REACTION_FILTER_STRATEGY_TO_REACTION_MAP[state.current_filter] === reaction &&
            state.participant_bar_user_ids.includes(userId)
          )
        ) {
          state.users[userId].reaction = null;
        }
      })
      .addCase(setUserIsSpeaking.fulfilled, (state, action) => {
        // lower hand
        const { userId, isSpeaking } = action.payload;
        if (userId && isSpeaking === state.users[userId].is_speaking) {
          state.users[userId].is_hand_raised = false;
        }
      });
  },
});

export const {
  join,
  leave,

  // Help
  sendPubsubMessage,
  softReset,

  // Participant bar
  replaceUserInParticipantBar,
  setCurrentFilter,

  // Stage management
  featureBreakoutRoomResource,
  featureContentInOneUp,
  setFeaturedInModal,
  setStagePane,
  setStagePaneIsPinned,
  setAllStagePanes,
  clearAllStagePanes,

  // Classroom settings
  setActiveTimelineSegmentId,
  setAvService,
  setClassType,
  setEnableStudentDrawing,
  setIsInFlight,

  // AV
  setBandwidthModeForUser,
  setBlurBackground,
  setPttModeForUser,
  setPttModeForClass,
  setMaxSubscribedStreams,
  setUserCameraError,
  setUserConnectionQuality,
  setUserConnectionState,
  setUserIsPerformingAvCheck,
  setUserMicError,
  setUserPubsubConnectionQuality,
  setUserStreamState,
  setUserPreferredDevices,
  muteAllExceptInstructor,

  // Chat
  addChat,
  likeOrUnlikeChat,

  // Reactions and signals
  setUserVote,
  setUserZoomPreference,
  toggleUserHandRaised,
  clearAllSignals,

  // Recordings
  setRecordingSessionId,
  setRecordingState,

  // Breakouts
  addBreakout,
  clearAllBreakoutGroupTitles,
  setAllBreakoutGroupTitles,
  setBreakoutRooms,
  setResources,
  startBreakoutSession,
  adjustBreakoutSessionDuration,
  moveBreakoutRoomUser,
  assignUserToLeastPopulatedBreakoutRoom,
  setBreakoutRoomIsPinned,
  visitBreakoutRoom,
  stopVisitingBreakoutRoom,
  listenToBreakoutRoom,
  stopListeningToBreakoutRoom,
  endBreakoutSession,

  // Timer
  playTimer,
  pauseTimer,
  adjustTimerDuration,
  endTimer,

  // User
  addBreakoutTalkTime,
  addClassroomTalkTime,
  setUserHasClassroomInFocus,
  setUserPresenceData,
  setUserWhiteboardFontSizePreference,
  setUserWhiteboardStrokeSizePreference,

  // Polls
  createNewPoll,
  startPollSession,
  endPollSession,
  setPollSessionViewResultsAs,
  createOrUpdatePollResponse,
  makePollResponsesVisibleToStudents,
  featurePollResponse,
  hideFeaturedPollResponse,

  // Resources
  addResource,
  deleteScreenshareResource,
  setResourceOwnerId,
  setResourcePage,
  setResourcePresentationViewScrollPosition,
  setResourceTitle,
  setResourceVideoState,
  setResourceVideoTimeInSeconds,
  setResourceResetWhiteboard,

  // Live captions
  setUserEnableLiveCaptions,
  setLatestCaption,
} = classroomSlice.actions;

// Used in async thunk
const { _replaceUserOnStage, _setParticipantBarUserIds, _setUserReaction, _setUserIsSpeaking } =
  classroomSlice.actions;

const classroomSliceReducer = classroomSlice.reducer;

export type ClassroomSliceReducerType = typeof classroomSliceReducer;

export { classroomSliceReducer, getAssignedBreakoutRoomIdForUser };
